import * as React from 'react'
import { O, pipe, RA, t } from '@packages/prelude'
import { styled } from 'styled-components'
import { RotatingLines } from 'react-loader-spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'

export interface AdSlide {
  image: string
  alt: string
  link: O.Option<t.NonEmptyString>
}

export interface PreviewAdsProps {
  slides: ReadonlyArray<AdSlide>
  slideInterval: number
  canAdvance: boolean
  nextLabel: string
  onAdvance: () => void
}

const Stage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 7;
  overflow: hidden;
  border-radius: 0.5rem;
  background: #111;

  @media (max-width: 767px) {
    aspect-ratio: 9 / 16;
    max-width: 420px;
    margin: 0 auto;
  }
`

const Track = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  transition: transform 0.5s ease;
`

const SlideItem = styled.div`
  position: relative;
  flex: 0 0 100%;
  height: 100%;
`

const Slide = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Dots = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.35rem;
`

const Dot = styled.span<{ $active: boolean }>`
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.5)')};
`

const NextButton = styled.button`
  position: absolute;
  bottom: 0.9rem;
  left: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 999px;
  font-weight: 600;
  color: #fff;
  background: var(--primary, rgba(20, 20, 28, 0.85));
  cursor: pointer;

  &:disabled {
    background: rgba(20, 20, 28, 0.78);
    cursor: default;
  }

  @media (max-width: 767px) {
    left: 50%;
    transform: translateX(-50%);
  }
`

export const PreviewAds: React.FC<PreviewAdsProps> = ({ slides, slideInterval, canAdvance, nextLabel, onAdvance }) => {
  const [index, setIndex] = React.useState(0)

  React.useEffect(() => {
    if (slides.length <= 1) return
    const id = setInterval(() => setIndex(i => (i + 1) % slides.length), Math.max(1, slideInterval) * 1000)
    return () => clearInterval(id)
  }, [slides.length, slideInterval])

  return (
    <Stage>
      <Track style={{ transform: `translateX(-${index * 100}%)` }}>
        {pipe(
          slides,
          RA.map(slide => (
            <SlideItem key={slide.image}>
              {pipe(
                slide.link,
                O.fold(
                  () => <Slide src={slide.image} alt={slide.alt} />,
                  href => (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      <Slide src={slide.image} alt={slide.alt} />
                    </a>
                  )
                )
              )}
            </SlideItem>
          ))
        )}
      </Track>

      {slides.length > 1 && (
        <Dots>
          {pipe(
            slides,
            RA.mapWithIndex((i, slide) => <Dot key={slide.image} $active={i === index} />)
          )}
        </Dots>
      )}

      <NextButton type="button" disabled={!canAdvance} onClick={onAdvance} aria-label={nextLabel}>
        {canAdvance ? (
          <>
            {nextLabel}
            <FontAwesomeIcon icon={faArrowRight} />
          </>
        ) : (
          <RotatingLines strokeColor="#ffffff" width="22" />
        )}
      </NextButton>
    </Stage>
  )
}
